import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui-wrapper";
import { UlmTextField } from "@components/ulm/ulm-text-field";
import { UlmButton } from "@components/ulm/ulm-button";
import {
  isSelectedServerUseThirdPartyLogin,
  redirectToAnotherServerLoginPage,
} from "@utils/ulm-utils";
import { UlmContainer } from "@components/ulm/ulm-container";
import { useAppNavigation } from "@hooks/use-app-navigation";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { ulmConfigClientSelector } from "@store/ulm-config/ulm-config-selector";
import {
  fetchEmailServers,
  resetFetchedServers,
  setHasCheckPrefillEmail,
  setHasSelectedPreselectedServer,
} from "@store/login/login-slice";
import { useSphereDashboardLogin } from "@hooks/use-sphere-dashboard-login";
import { LoginServerSelector } from "@pages/login/login-server-selector";
import { Link, OpenToastOptions, Tooltip, useToast } from "@flat-ui";
import { UlmPasswordField } from "@components/ulm/ulm-password-field";
import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import {
  activeServersSelector,
  hasCheckPrefillEmailSelector,
  hasSelectedPreselectedServerSelector,
  isFetchingServersSelector,
  prefillEmailSelector,
  preselectedServerSelector,
} from "@store/login/login-selector";
import { redirectClient } from "@utils/client-utils";
import { Link as RouterLink } from "react-router-dom";
import { ContactSupport } from "@components/ulm/contact-support";
import { getErrorDisplayMarkup } from "@context-providers/error-handler/error-handler-utils";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { PageTitle } from "@components/common/page-title";
import { CLIENT_CONFIGURATION } from "@constants/ulm-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { AsyncUtils, DateTimeUtils, EmailUtils } from "@stellar/utils";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { isAccountBlockedError } from "@utils/login-config-utils";
import { setAutomationTestUserTag } from "@utils/sentry-utils";

/** Text to show in `Start free trial` tooltip */
const START_FREE_TRIAL_TEXT =
  "Access our 21-day free trial to explore Sphere XG. " +
  "See just how easy it is to navigate & collaborate.";

/**
 * Login page component.
 */
export function Login(): JSX.Element {
  const dispatch = useAppDispatch();
  const client = useAppSelector(ulmConfigClientSelector);
  const prefillEmail = useAppSelector(prefillEmailSelector);
  const hasCheckPrefillEmail = useAppSelector(hasCheckPrefillEmailSelector);
  const preselectedServer = useAppSelector(preselectedServerSelector);
  const hasSelectedPreselectedServer = useAppSelector(
    hasSelectedPreselectedServerSelector
  );

  // Hide temporarily the trial registration workflow.
  // TODO Show trial registration again: https://faro01.atlassian.net/browse/ST-5453
  // const shouldHideRegistration= useAppSelector(shouldHideRegistrationSelector);
  const shouldHideRegistration = true;
  const activeServers = useAppSelector(activeServersSelector);
  const isFetchingServers = useAppSelector(isFetchingServersSelector);
  const { navigateToTrialSignup } = useAppNavigation();
  const { visitedServerIdentifier, loginWithEmail, loginWithProvider } =
    useSphereDashboardLogin();
  const { openToast, closeToast } = useToast();
  const { handleError } = useErrorHandlerContext();

  // Whether a backend request for the login is ongoing.
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Whether a redirection outside the ULM is ongoing.
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  // Raw email input value
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  // Whether the value in the email input has an invalid email format
  const [isEmailInputError, setIsEmailInputError] = useState<boolean>(false);

  // The selected server identifier
  const [selectedServerIdentifier, setSelectedServerIdentifier] =
    useState<FaroCoreApiTypes.ServerIdentifier | null>(null);

  // Sanitized email input value
  const sanitizedEmail = useMemo(() => {
    return email.trim();
  }, [email]);

  // Whether the sanitized email input value has a valid email format
  const isValidEmailFormat = useMemo(() => {
    return EmailUtils.isValidEmail(sanitizedEmail);
  }, [sanitizedEmail]);

  // Whether user has more than one active servers and the server selector UI should be shown
  const shouldShowServerSelector = useMemo(() => {
    return activeServers && activeServers.length > 1;
  }, [activeServers]);

  // Whether the continue button should be shown
  const shouldShowContinueButton = useMemo(() => {
    return !(activeServers && activeServers.length > 0);
  }, [activeServers]);

  // The selected server
  const selectedServer = useMemo(() => {
    if (activeServers && activeServers.length > 0) {
      return activeServers.find(
        (server) => server.identifier === selectedServerIdentifier
      );
    }
  }, [activeServers, selectedServerIdentifier]);

  // Show password field if selected server uses login with email and the email input is valid
  // and there is no redirection
  const shouldShowPasswordField = useMemo(() => {
    return (
      isValidEmailFormat &&
      selectedServer?.loginProvider?.identifier === "email" &&
      selectedServer.identifier === visitedServerIdentifier
    );
  }, [isValidEmailFormat, selectedServer, visitedServerIdentifier]);

  // True iff the preselected Server is one of the active servers
  const isValidPreselectedServer = useMemo(() => {
    if (!activeServers || !preselectedServer) {
      return false;
    }

    const activeServersIds = activeServers.map((server) => server.identifier);
    return activeServersIds.includes(preselectedServer);
  }, [activeServers, preselectedServer]);

  /** Determine the forgot password link based on the selected server */
  const forgotPasswordLink = useCallback(
    (text: string) => {
      const redirectionServer =
        selectedServerIdentifier ?? visitedServerIdentifier;

      // Early return if redirectionServer is not selected
      if (!redirectionServer) {
        return undefined;
      }

      const encodedEmail = encodeURIComponent(email);

      return (
        <Link
          component={RouterLink}
          to={`/passwordRecovery?email=${encodedEmail}`}
          underline="hover"
        >
          {text}
        </Link>
      );
    },
    [email, selectedServerIdentifier, visitedServerIdentifier]
  );

  // Triggered on every change in the email input
  function onChange(event: React.ChangeEvent<HTMLInputElement>): void {
    event.stopPropagation();
    setEmail(event.target.value);

    // Reset password field, selected server, fetched servers and warnings
    setPassword("");
    setSelectedServerIdentifier(null);
    dispatch(resetFetchedServers());
    closeToast();
  }

  // If the `enter` key was pressed then do the same as when the continue button is clicked
  function onEmailKeyUp(event: React.KeyboardEvent<HTMLInputElement>): void {
    event.stopPropagation();
    if (event.key === "Enter") {
      void getUserServers();
    }
  }

  // If the `enter` key was pressed then do the same as when the login button is clicked
  function onPasswordKeyUp(event: React.KeyboardEvent<HTMLInputElement>): void {
    event.stopPropagation();
    if (event.key === "Enter") {
      void onLogInClick();
    }
  }

  // Triggered when the login button is clicked
  async function onLogInClick(
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event && event.stopPropagation();

    // Early return if selected server identifier and password is not available
    if (!selectedServerIdentifier || !password.length || !isValidEmailFormat) {
      return;
    }

    setIsLoading(true);

    /**
     * Re-sends the verification email to the email address provided in the email input.
     */
    async function resendVerificationEmail(): Promise<void> {
      try {
        await faroCoreApiClient.users.resendVerificationMail(sanitizedEmail);
        // Wait for a second to finish closing the first error message toast
        // and to give a bit of time for the email to be sent.
        await AsyncUtils.delay(DateTimeUtils.MILLISECONDS_IN_SECOND);
        openToast({
          title: "Verification email sent",
          message:
            "A new verification email has been sent.\nPlease check your inbox",
        });
      } catch (error) {
        const title = "Failed to resend verification email.";
        openToast({
          title,
          message: getErrorDisplayMarkup(error),
          variant: "error",
        });
      }
    }

    try {
      await loginWithEmail({
        selectedServer: selectedServerIdentifier,
        email: sanitizedEmail,
        password,
      });
    } catch (error) {
      const title = "Failed to login.";

      // Checks if the reason for the failed login is that the account is blocked
      if (isAccountBlockedError(error)) {
        openToast({
          title,
          message: getErrorDisplayMarkup(error),
          variant: "error",
          action: {
            label: "Resend email",
            onClicked: () => {
              void resendVerificationEmail();
            },
          },
        });
      } else {
        handleError({
          id: `loginWithEmail-${Date.now().toString()}`,
          title,
          error,
          uiType: "toast",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  // Requests the servers for the sanitized email value and sets the active servers
  const getUserServers = useCallback(async (): Promise<void> => {
    // close toast messages
    closeToast();

    // If the email format is invalid do an early exit
    if (!isValidEmailFormat) {
      // Set email input in error state
      setIsEmailInputError(true);
      return;
    }

    await dispatch(
      fetchEmailServers({
        email: sanitizedEmail,
      })
    );
  }, [closeToast, isValidEmailFormat, dispatch, sanitizedEmail]);

  function handleSignupClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    event.stopPropagation();

    // If the trial is handled by SalesForce then redirect to the official trial page,
    // otherwise navigate to the ULM signup page
    if (CLIENT_CONFIGURATION[client].isSalesForceTrial) {
      try {
        redirectClient({
          client,
          redirectUrl: "https://www.holobuilder.com/free-trial",
        });
      } catch (error) {
        openToast({
          title: "Redirection failed",
          message: getErrorDisplayMarkup(error),
          variant: "error",
        });
      }
    } else {
      navigateToTrialSignup();
    }
  }

  // Resets the email input error state if the input it's cleared or if it has a valid email format
  useEffect(() => {
    if (sanitizedEmail === "" || isValidEmailFormat) {
      setIsEmailInputError(false);
    }
  }, [isValidEmailFormat, sanitizedEmail]);

  // Tag Sentry events with whether the email is an internal FARO automation/test account so those
  // logs can be dropped (see sentry-init `beforeSend`); recompute on every change, defaulting to "false".
  useEffect(() => {
    setAutomationTestUserTag(isValidEmailFormat ? sanitizedEmail : "");
  }, [isValidEmailFormat, sanitizedEmail]);

  // Handles the automatic email check if there is a valid prefilled email
  useEffect(() => {
    if (prefillEmail && !hasCheckPrefillEmail) {
      if (!sanitizedEmail) {
        setEmail(prefillEmail);
      } else {
        if (isValidEmailFormat) {
          void getUserServers();
        } else {
          setIsEmailInputError(true);
        }
        dispatch(setHasCheckPrefillEmail(true));
      }
    }
  }, [
    dispatch,
    getUserServers,
    hasCheckPrefillEmail,
    prefillEmail,
    sanitizedEmail,
    isValidEmailFormat,
  ]);

  // Show account banner if there is no active server with the email
  useEffect(() => {
    if (activeServers && activeServers.length === 0) {
      openToast({
        title: "No account found for this email",
        variant: "warning",
      });
    }
  }, [activeServers, openToast]);

  // Handles the automatic server selection if there is only one active server
  useEffect(() => {
    if (activeServers && activeServers.length === 1) {
      const serverIdentifier = activeServers[0].identifier;
      setSelectedServerIdentifier(serverIdentifier);
    }
  }, [activeServers]);

  // Handles the automatic server selection if there is a valid preselected server
  useEffect(() => {
    if (preselectedServer && !hasSelectedPreselectedServer && activeServers) {
      // Only set the preselected if it's valid and if there are more than one active servers
      if (isValidPreselectedServer && activeServers.length > 1) {
        setSelectedServerIdentifier(preselectedServer);
      }
      dispatch(setHasSelectedPreselectedServer(true));
    }
  }, [
    preselectedServer,
    isValidPreselectedServer,
    activeServers,
    hasSelectedPreselectedServer,
    dispatch,
  ]);

  /**
   * Handles all redirects that can happen after a server is selected.
   * The order of the checks is important. Be mindful before changing them. Current order:
   * 1. SSO login providers for Sphere XG. Re redirection can be to both servers.
   * 2. Email/password login for Sphere XG but in a different server from the current one
   */
  useEffect(() => {
    if (!selectedServer) {
      return;
    }

    const redirectToast: OpenToastOptions = {
      title: "You are being redirected...",
      variant: "neutral",
    };

    // SSO login providers for Sphere XG
    if (isSelectedServerUseThirdPartyLogin(selectedServer)) {
      openToast(redirectToast);
      setIsRedirecting(true);
      loginWithProvider(selectedServer);
      return;
    }

    // Email/password login for Sphere XG but in a different server from the current one
    if (selectedServer.identifier !== visitedServerIdentifier) {
      openToast(redirectToast);
      setIsRedirecting(true);
      redirectToAnotherServerLoginPage({
        selectedServer: selectedServer.identifier,
        email: sanitizedEmail,
        client,
      });
      return;
    }
  }, [
    client,
    loginWithProvider,
    openToast,
    sanitizedEmail,
    selectedServer,
    visitedServerIdentifier,
  ]);

  return (
    <UlmContainer>
      <PageTitle>Log in</PageTitle>

      {/* Start free trial */}
      {!shouldHideRegistration && (
        <Box
          sx={{
            marginTop: "10px",
            fontSize: "14px",
            lineHeight: "24px",
            letterSpacing: "0px",
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
          }}
        >
          New to Sphere XG?
          <Tooltip
            title={START_FREE_TRIAL_TEXT}
            tooltipSx={{
              maxWidth: "215px",
            }}
          >
            <Link
              component="button"
              underline="none"
              onClick={handleSignupClick}
              sx={{ marginLeft: "5px" }}
            >
              Start free trial.
            </Link>
          </Tooltip>
        </Box>
      )}

      {/* Email input */}
      <Stack
        sx={{
          width: "100%",
          marginTop: shouldHideRegistration ? "74px" : "40px",
        }}
      >
        <UlmInputLabel title="Email" />
        <UlmTextField
          value={email}
          type="email"
          name="email"
          autoComplete="email"
          isValidValue={isValidEmailFormat}
          isError={isEmailInputError}
          errorText={"Enter a valid email format"}
          placeholder={"Enter your email"}
          onChange={onChange}
          onKeyUp={onEmailKeyUp}
          isDisabled={isFetchingServers || isLoading || isRedirecting}
        />
      </Stack>

      {/* Continue Button to confirm email input */}
      <UlmButton
        disabled={sanitizedEmail === "" || isRedirecting}
        isLoading={isFetchingServers}
        isLoadingText="Verifying"
        onClick={getUserServers}
        sx={{
          marginTop: "8px",
          display: shouldShowContinueButton ? "inline-flex" : "none",
        }}
      >
        Continue
      </UlmButton>

      {/* Server selector */}
      {shouldShowServerSelector && activeServers?.length && (
        <LoginServerSelector
          servers={activeServers.map((server) => server.identifier)}
          selectedServer={selectedServerIdentifier}
          onSelectServer={setSelectedServerIdentifier}
          isDisabled={isLoading || isRedirecting}
        />
      )}

      {/* Password input */}
      <Stack
        sx={{
          width: "100%",
          // Password field is hidden by css to get the browsers autocomplete
          display: shouldShowPasswordField ? "inline-flex" : "none",
        }}
      >
        <UlmPasswordField
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyUp={onPasswordKeyUp}
          helpComponent={
            shouldShowPasswordField
              ? forgotPasswordLink("Forgot password?")
              : null
          }
          isDisabled={isRedirecting}
        />
      </Stack>

      {/* Login Button */}
      <UlmButton
        disabled={password === "" || isRedirecting}
        isLoading={isLoading}
        isLoadingText="Logging In"
        onClick={onLogInClick}
        sx={{
          marginTop: "8px",
          display: shouldShowPasswordField ? "inline-flex" : "none",
        }}
      >
        Log in
      </UlmButton>

      {/* Support */}
      <Box
        component="div"
        sx={{
          marginBottom: "10px",
          textAlign: "center",
          fontSize: "14px",
          flex: 1,
          alignContent: "end",
          marginTop: "20px",
        }}
      >
        Can’t log in? Contact <ContactSupport />
      </Box>
    </UlmContainer>
  );
}
